import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import NotificationDialog from "widgets/dialogs/notification-dialog";

type NotificationResult = "ok" | "cancel" | "close";

type ShowNotificationOptions = {
  header: string;
  message: string;
  okText?: string;
  cancelText?: string;
  onOk?: () => void;
  onCancel?: () => void;
};

type NotificationDialogContextValue = {
  notify: (options: ShowNotificationOptions) => Promise<NotificationResult>;
};

const NotificationDialogContext = createContext<
  NotificationDialogContextValue | undefined
>(undefined);

export const NotificationDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [header, setHeader] = useState("");
  const [message, setMessage] = useState("");
  const [okText, setOkText] = useState<string | undefined>(undefined);
  const [cancelText, setCancelText] = useState<string | undefined>(undefined);

  const resolveRef = useRef<((value: NotificationResult) => void) | null>(null);
  const onOkRef = useRef<(() => void) | undefined>(undefined);
  const onCancelRef = useRef<(() => void) | undefined>(undefined);

  const closeWith = useCallback((result: NotificationResult) => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const notify = useCallback((options: ShowNotificationOptions) => {
    setHeader(options.header);
    setMessage(options.message);
    setOkText(options.okText);
    setCancelText(options.cancelText);
    onOkRef.current = options.onOk;
    onCancelRef.current = options.onCancel;
    setIsOpen(true);

    return new Promise<NotificationResult>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationDialogContext.Provider value={value}>
      {children}
      <NotificationDialog
        isOpen={isOpen}
        header={header}
        message={message}
        onClose={() => closeWith("close")}
        onCancel={
          onCancelRef.current
            ? () => {
                onCancelRef.current?.();
                closeWith("cancel");
              }
            : undefined
        }
        onOk={
          onOkRef.current
            ? () => {
                onOkRef.current?.();
                closeWith("ok");
              }
            : undefined
        }
        okText={okText}
        cancelText={cancelText}
      />
    </NotificationDialogContext.Provider>
  );
};

export const useNotificationDialog = (): NotificationDialogContextValue => {
  const ctx = useContext(NotificationDialogContext);
  if (!ctx) {
    throw new Error(
      "useNotificationDialog must be used within a NotificationDialogProvider"
    );
  }
  return ctx;
};
